"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebNotificationService = void 0;
/**
 * Web Notification Service (duplicate)
 *
 * This is a Firestore-based notification service that queries the
 * 'notifications' collection. Moved to shared for cross-platform use.
 * Uses UnifiedFirestore for cross-platform compatibility (web SDK on browser, native plugin on iOS/Android).
 */
const firestore_1 = require("firebase/firestore");
const firebase_1 = require("./firebase");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
const core_1 = require("@capacitor/core");
class WebNotificationService {
    static subscribeToNotifications(userId, callback, limitCount = 50) {
        if (!(0, firebase_1.isFirebaseInitialized)()) {
            console.error('Firebase not initialized for notifications subscription');
            return () => { };
        }
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            // Native platforms don't support onSnapshot - use polling
            console.log('📱 WebNotificationService: Using polling on native');
            let isActive = true;
            // Initial fetch
            this.getUserNotifications(userId, limitCount).then(callback).catch(() => callback([]));
            const intervalId = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                if (isActive) {
                    try {
                        const notifications = yield this.getUserNotifications(userId, limitCount);
                        callback(notifications);
                    }
                    catch (error) {
                        console.warn('Notification polling error:', error);
                    }
                }
            }), 30000);
            return () => {
                isActive = false;
                clearInterval(intervalId);
            };
        }
        // Web platform - use real-time onSnapshot
        const notificationsQuery = (0, firestore_1.query)((0, firestore_1.collection)((0, firebase_1.getDb)(), 'notifications'), (0, firestore_1.where)('userId', '==', userId), (0, firestore_1.orderBy)('timestamp', 'desc'), (0, firestore_1.limit)(limitCount));
        return (0, firestore_1.onSnapshot)(notificationsQuery, (snapshot) => {
            const notifications = snapshot.docs.map(doc => (Object.assign({ id: doc.id }, doc.data())));
            callback(notifications);
        });
    }
    static getUserNotifications(userId, limitCount = 50) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, firebase_1.isFirebaseInitialized)()) {
                return [];
            }
            try {
                // Use unified collection for cross-platform support
                const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('notifications')
                    .where('userId', '==', userId)
                    .orderBy('timestamp', 'desc')
                    .limit(limitCount)
                    .getDocs();
                return snapshot.map(doc => (Object.assign({ id: doc.id }, doc.data())));
            }
            catch (error) {
                console.error('Error querying notifications:', error);
                throw error;
            }
        });
    }
    static getUnreadCount(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, firebase_1.isFirebaseInitialized)()) {
                return 0;
            }
            try {
                // Use unified collection for cross-platform support
                const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('notifications')
                    .where('userId', '==', userId)
                    .where('read', '==', false)
                    .getDocs();
                return snapshot.length;
            }
            catch (error) {
                console.error('Error getting unread count:', error);
                return 0;
            }
        });
    }
    static markAsRead(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('notifications', notificationId, { read: true });
        });
    }
    static markAllAsRead(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('notifications')
                .where('userId', '==', userId)
                .where('read', '==', false)
                .getDocs();
            const updatePromises = snapshot.map(doc => (0, unifiedFirestoreService_1.unifiedUpdateDoc)('notifications', doc.id, { read: true }));
            yield Promise.all(updatePromises);
        });
    }
    static deleteNotification(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)('notifications', notificationId);
        });
    }
    static formatNotificationMessage(notification) {
        return notification.message;
    }
    static getNotificationIcon(type) {
        switch (type) {
            case 'movie_added': return '🎬';
            case 'collaborator_added': return '👥';
            case 'movie_watched': return '✅';
            case 'list_shared': return '📋';
            case 'user_followed': return '👤';
            case 'watchlist_liked': return '❤️';
            case 'watch_voice_movie_added': return '⌚';
            default: return '🔔';
        }
    }
    static getNotificationActionPath(notification) {
        switch (notification.type) {
            case 'movie_added':
            case 'movie_watched':
            case 'watch_voice_movie_added':
                if (notification.relatedWatchlistId) {
                    return `/watchlist/${notification.relatedWatchlistId}`;
                }
                if (notification.relatedMovieId) {
                    return `/movie/${notification.relatedMovieId}`;
                }
                break;
            case 'collaborator_added':
            case 'list_shared':
            case 'watchlist_liked':
                if (notification.relatedWatchlistId) {
                    return `/watchlist/${notification.relatedWatchlistId}`;
                }
                break;
            case 'user_followed':
                if (notification.relatedUserId) {
                    return `/user/${notification.relatedUserId}`;
                }
                break;
        }
        return '/notifications';
    }
}
exports.WebNotificationService = WebNotificationService;
